import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import VideoCarouselModal, { VideoCarousel } from '../components/VideoCarousel/VideoCarouselModal';
import { useFotbollPlayTeamChannels } from './useFotbollPlayTeamChannels';
import { useSnackbar } from 'notistack';
import { useAppConfig } from '../../../../hooks/appConfigs';
import { useStringsContext } from '../../../../contexts/strings';

interface VideoCarouselsContextValue {
  carousels: VideoCarousel[];
  openCreateCarousel: () => void;
  openEditCarousel: (carousel: VideoCarousel) => void;
  deleteCarousel: (carouselId: string) => void;
}

const VideoCarouselsContext = createContext<VideoCarouselsContextValue | null>(null);

interface VideoCarouselsProviderProps {
  teamAbbrv: string;
  initialCarousels?: VideoCarousel[];
  children: ReactNode;
}

export function VideoCarouselsProvider({
  teamAbbrv,
  initialCarousels = [],
  children,
}: VideoCarouselsProviderProps) {
  const [carousels, setCarousels] = useState<VideoCarousel[]>(initialCarousels);
  const [isCarouselModalOpen, setIsCarouselModalOpen] = useState(false);
  const [editingCarousel, setEditingCarousel] = useState<VideoCarousel | null>(null);
  const { channels } = useFotbollPlayTeamChannels(teamAbbrv);
  const { appConfig } = useAppConfig(teamAbbrv);
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();

  useEffect(() => {
    setCarousels(initialCarousels);
  }, [initialCarousels]);

  const openCreateCarousel = useCallback(() => {
    setEditingCarousel(null);
    setIsCarouselModalOpen(true);
  }, []);

  const openEditCarousel = useCallback((carousel: VideoCarousel) => {
    setEditingCarousel(carousel);
    setIsCarouselModalOpen(true);
  }, []);

  const closeCarouselModal = useCallback(() => {
    setIsCarouselModalOpen(false);
    setEditingCarousel(null);
  }, []);

  const saveCarousel = useCallback(
    (carousel: VideoCarousel) => {
      setCarousels((prev) => {
        const existingIndex = prev.findIndex((c) => c.id === carousel.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = carousel;
          return updated;
        }
        return [...prev, carousel];
      });

      // The carousel is only kept locally until the tab is published
      enqueueSnackbar(
        `Karusellen är inte sparad än. Tryck på "${strings.actions.publishChanges}" för att spara.`,
        { variant: 'warning' }
      );
    },
    [enqueueSnackbar, strings.actions.publishChanges]
  );

  const deleteCarousel = useCallback((carouselId: string) => {
    setCarousels((prev) => prev.filter((c) => c.id !== carouselId));
  }, []);

  return (
    <VideoCarouselsContext.Provider
      value={{
        carousels,
        openCreateCarousel,
        openEditCarousel,
        deleteCarousel,
      }}
    >
      {children}
      <VideoCarouselModal
        open={isCarouselModalOpen}
        onClose={closeCarouselModal}
        onSave={saveCarousel}
        editCarousel={editingCarousel}
        channels={channels}
        league={appConfig?.configLeagueName || undefined}
        teamAbbrv={teamAbbrv}
      />
    </VideoCarouselsContext.Provider>
  );
}

export function useVideoCarousels(): VideoCarouselsContextValue {
  const context = useContext(VideoCarouselsContext);
  if (!context) {
    throw new Error('useVideoCarousels must be used within VideoCarouselsProvider');
  }
  return context;
}
